/**
 * Ductulator - Visual rotating wheel duct size calculator
 * Mimics the classic physical HVAC ductulator (circular slide rule)
 */

import React, { useState, useRef, useCallback, useMemo } from 'react';
import { FloatingPanel } from '../ui/Panel';

// ===== Constants =====
const CFM_VALUES = [50, 75, 100, 150, 200, 250, 300, 350, 400, 500, 600, 800, 1000, 1200, 1500, 2000];
const FRICTION_RATES = [0.05, 0.06, 0.08, 0.10, 0.15, 0.20];
const STANDARD_ROUND_SIZES = [4, 5, 6, 7, 8, 9, 10, 12, 14, 16, 18, 20, 24];

const LOG_MIN = Math.log(CFM_VALUES[0]);
const LOG_MAX = Math.log(CFM_VALUES[CFM_VALUES.length - 1]);
const LOG_RANGE = LOG_MAX - LOG_MIN;
const ARC_SPAN = 330; // degrees of arc used for CFM scale

const CX = 170, CY = 170; // SVG center

// ===== Angle/CFM mapping (logarithmic scale like physical ductulator) =====
function cfmToAngle(cfm) {
  return ((Math.log(cfm) - LOG_MIN) / LOG_RANGE) * ARC_SPAN;
}

function angleToCfm(angle) {
  angle = ((angle % 360) + 360) % 360;
  if (angle > ARC_SPAN) {
    // In the dead zone — snap to nearest end
    return angle < (ARC_SPAN + (360 - ARC_SPAN) / 2) ? CFM_VALUES[CFM_VALUES.length - 1] : CFM_VALUES[0];
  }
  const logCfm = LOG_MIN + (angle / ARC_SPAN) * LOG_RANGE;
  const cfm = Math.exp(logCfm);
  return CFM_VALUES.reduce((prev, curr) =>
    Math.abs(curr - cfm) < Math.abs(prev - cfm) ? curr : prev
  );
}

// ===== Polar to cartesian helper =====
function polarToXY(angleDeg, radius) {
  const rad = (angleDeg - 90) * (Math.PI / 180); // -90 so 0° = top
  return {
    x: CX + radius * Math.cos(rad),
    y: CY + radius * Math.sin(rad),
  };
}

// ===== HVAC Calculation Functions =====

/** Round duct diameter from CFM and friction rate */
function calculateDuctDiameter(cfm, frictionRate) {
  if (cfm <= 0 || frictionRate <= 0) return 0;
  return Math.pow((0.109136 * Math.pow(cfm, 1.9)) / frictionRate, 1 / 5.02);
}

/** Velocity in FPM */
function calculateVelocity(cfm, diameterInches) {
  if (diameterInches <= 0) return 0;
  const areaFt2 = Math.PI * Math.pow(diameterInches / 2 / 12, 2);
  return cfm / areaFt2;
}

/** Round up to nearest standard duct size */
function getStandardDuctSize(exactDiameter) {
  for (const size of STANDARD_ROUND_SIZES) {
    if (size >= exactDiameter) return size;
  }
  return STANDARD_ROUND_SIZES[STANDARD_ROUND_SIZES.length - 1];
}

/** Rectangular equivalent diameter */
function calcRectEquivalent(a, b) {
  return 1.3 * Math.pow(a * b, 0.625) / Math.pow(a + b, 0.25);
}

/** Find rectangular duct sizes matching a round diameter */
function findRectEquivalents(targetDiameter) {
  const results = [];
  const sides = [4, 6, 8, 10, 12, 14, 16, 18, 20, 24];
  for (let i = 0; i < sides.length; i++) {
    for (let j = i; j < sides.length; j++) {
      const de = calcRectEquivalent(sides[i], sides[j]);
      if (Math.abs(de - targetDiameter) <= 1.0) {
        results.push({ w: sides[i], h: sides[j], de: de.toFixed(1) });
      }
    }
  }
  // Sort by aspect ratio (prefer squarer ducts first)
  results.sort((a, b) => {
    const ra = Math.max(a.w, a.h) / Math.min(a.w, a.h);
    const rb = Math.max(b.w, b.h) / Math.min(b.w, b.h);
    return ra - rb;
  });
  return results;
}

// ===== Velocity color coding =====
function getVelocityColor(v) {
  if (v < 700) return '#4caf50';
  if (v < 900) return '#ffffff';
  if (v < 1100) return '#ff9800';
  return '#f44336';
}

function getVelocityLabel(v) {
  if (v < 700) return 'Quiet';
  if (v < 900) return 'OK';
  if (v < 1100) return 'Moderate';
  return 'Noisy';
}

// ===== Component =====
export default function Ductulator({ onClose, isMobile = false }) {
  const initialCfm = 400;
  const [selectedCFM, setSelectedCFM] = useState(initialCfm);
  const [rotationOffset, setRotationOffset] = useState(() => (360 - cfmToAngle(initialCfm)) % 360);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartAngleRef = useRef(0);
  const [frictionRate, setFrictionRate] = useState(0.08);
  const svgRef = useRef(null);

  // ===== Computed results =====
  const results = useMemo(() => {
    const exactD = calculateDuctDiameter(selectedCFM, frictionRate);
    const standardD = getStandardDuctSize(exactD);
    const velocity = Math.round(calculateVelocity(selectedCFM, standardD));
    const rectEquivs = findRectEquivalents(standardD);
    return { exactD, standardD, velocity, rectEquivs };
  }, [selectedCFM, frictionRate]);

  const velocityColor = getVelocityColor(results.velocity);
  const velocityLabel = getVelocityLabel(results.velocity);

  // ===== Pointer handlers for wheel rotation =====
  const getAngle = useCallback((clientX, clientY) => {
    const rect = svgRef.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    let angle = Math.atan2(clientX - cx, -(clientY - cy)) * (180 / Math.PI);
    if (angle < 0) angle += 360;
    return angle;
  }, []);

  const handlePointerDown = useCallback((e) => {
    if (!svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dist = Math.sqrt((e.clientX - cx) ** 2 + (e.clientY - cy) ** 2);
    const svgScale = rect.width / 340;
    const distInSvg = dist / svgScale;

    // Only start drag if pointer is on the outer ring area (radius 100-170)
    if (distInSvg < 85 || distInSvg > 175) return;

    e.target.setPointerCapture(e.pointerId);
    e.preventDefault();
    setIsDragging(true);
    dragStartAngleRef.current = getAngle(e.clientX, e.clientY) - rotationOffset;
  }, [getAngle, rotationOffset]);

  const handlePointerMove = useCallback((e) => {
    if (!isDragging) return;
    e.preventDefault();
    const cur = getAngle(e.clientX, e.clientY);
    let newOff = ((cur - dragStartAngleRef.current) % 360 + 360) % 360;
    setRotationOffset(newOff);
    const unrotated = ((360 - newOff) % 360 + 360) % 360;
    setSelectedCFM(angleToCfm(unrotated));
  }, [isDragging, getAngle]);

  const handlePointerUp = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);
    // Snap to exact CFM position
    const snapped = (360 - cfmToAngle(selectedCFM)) % 360;
    setRotationOffset(snapped);
  }, [isDragging, selectedCFM]);

  // ===== Render =====
  return (
    <FloatingPanel
      title="Ductulator"
      onClose={onClose}
      isMobile={isMobile}
      width={340}
      maxHeight={680}
      x={isMobile ? 10 : 60}
      y={isMobile ? 40 : 80}
      style={isMobile ? { width: Math.min(340, window.innerWidth - 20) + 'px' } : {}}
    >
      <div style={{ padding: '8px', touchAction: 'none' }}>

        {/* ===== SVG Wheel ===== */}
        <svg
          ref={svgRef}
          viewBox="0 0 340 340"
          width="100%"
          style={{
            maxWidth: '340px',
            margin: '0 auto',
            display: 'block',
            cursor: isDragging ? 'grabbing' : 'grab',
            userSelect: 'none',
            WebkitUserSelect: 'none',
          }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
        >
          <defs>
            <filter id="duct-glow">
              <feGaussianBlur stdDeviation="2" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <radialGradient id="centerGrad" cx="50%" cy="40%" r="60%">
              <stop offset="0%" stopColor="#1a2535" />
              <stop offset="100%" stopColor="#0a0f18" />
            </radialGradient>
          </defs>

          {/* Background */}
          <circle cx={CX} cy={CY} r="165" fill="#080c10" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />

          {/* ===== OUTER RING (Rotatable) ===== */}
          <g
            transform={`rotate(${rotationOffset}, ${CX}, ${CY})`}
            style={{ transition: isDragging ? 'none' : 'transform 0.2s ease-out' }}
          >
            {/* Ring background */}
            <circle cx={CX} cy={CY} r="155" fill="none" stroke="rgba(0,200,255,0.08)" strokeWidth="35" />
            {/* Ring edges */}
            <circle cx={CX} cy={CY} r="155" fill="none" stroke="rgba(0,200,255,0.12)" strokeWidth="1" />
            <circle cx={CX} cy={CY} r="120" fill="none" stroke="rgba(0,200,255,0.12)" strokeWidth="1" />

            {/* CFM tick marks and labels */}
            {CFM_VALUES.map((cfm) => {
              const angle = cfmToAngle(cfm);
              const isSelected = cfm === selectedCFM;
              const outerTick = polarToXY(angle, 155);
              const innerTick = polarToXY(angle, isSelected ? 118 : 122);
              const labelPos = polarToXY(angle, 137);

              // Rotate text so it reads outward, flip if on bottom half
              let textRotation = angle;
              if (angle > 90 && angle < 270) textRotation += 180;

              return (
                <g key={cfm}>
                  <line
                    x1={innerTick.x} y1={innerTick.y}
                    x2={outerTick.x} y2={outerTick.y}
                    stroke={isSelected ? '#00c8ff' : 'rgba(255,255,255,0.4)'}
                    strokeWidth={isSelected ? 2.5 : 1}
                  />
                  <text
                    x={labelPos.x} y={labelPos.y}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fill={isSelected ? '#00c8ff' : '#8090a0'}
                    fontSize={isSelected ? '12' : '9'}
                    fontWeight={isSelected ? '700' : '400'}
                    fontFamily="system-ui, sans-serif"
                    transform={`rotate(${textRotation}, ${labelPos.x}, ${labelPos.y})`}
                    filter={isSelected ? 'url(#duct-glow)' : undefined}
                  >
                    {cfm >= 1000 ? `${cfm / 1000}k` : cfm}
                  </text>
                </g>
              );
            })}

            {/* Minor tick marks between CFM values */}
            {CFM_VALUES.slice(0, -1).map((cfm, i) => {
              const a1 = cfmToAngle(cfm);
              const a2 = cfmToAngle(CFM_VALUES[i + 1]);
              const midAngle = (a1 + a2) / 2;
              const outer = polarToXY(midAngle, 155);
              const inner = polarToXY(midAngle, 148);
              return (
                <line key={`minor-${i}`}
                  x1={inner.x} y1={inner.y}
                  x2={outer.x} y2={outer.y}
                  stroke="rgba(255,255,255,0.15)"
                  strokeWidth="0.5"
                />
              );
            })}
          </g>

          {/* ===== FIXED POINTER at top ===== */}
          <polygon
            points={`${CX},${CY - 163} ${CX - 7},${CY - 175} ${CX + 7},${CY - 175}`}
            fill="#00c8ff"
            filter="url(#duct-glow)"
          />
          <line
            x1={CX} y1={CY - 163}
            x2={CX} y2={CY - 155}
            stroke="#00c8ff" strokeWidth="2"
          />

          {/* ===== MIDDLE RING (Fixed — duct size reference) ===== */}
          <circle cx={CX} cy={CY} r="118" fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth="33" />
          <circle cx={CX} cy={CY} r="118" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="0.5" />
          <circle cx={CX} cy={CY} r="85" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="0.5" />

          {/* Duct diameter markers on middle ring */}
          {STANDARD_ROUND_SIZES.map((size) => {
            const fraction = (size - 4) / (24 - 4);
            const angle = fraction * 300 + 30; // spread 300° starting at 30°
            const outer = polarToXY(angle, 118);
            const inner = polarToXY(angle, 100);
            const labelP = polarToXY(angle, 107);
            const isHighlighted = size === results.standardD;

            let textRotation = angle;
            if (angle > 90 && angle < 270) textRotation += 180;

            return (
              <g key={`duct-${size}`}>
                <line
                  x1={inner.x} y1={inner.y}
                  x2={outer.x} y2={outer.y}
                  stroke={isHighlighted ? '#ff6b35' : 'rgba(255,255,255,0.15)'}
                  strokeWidth={isHighlighted ? 2 : 0.5}
                />
                <text
                  x={labelP.x} y={labelP.y}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fill={isHighlighted ? '#ff6b35' : '#506070'}
                  fontSize={isHighlighted ? '10' : '7'}
                  fontWeight={isHighlighted ? '700' : '400'}
                  fontFamily="system-ui, sans-serif"
                  transform={`rotate(${textRotation}, ${labelP.x}, ${labelP.y})`}
                >
                  {size}"
                </text>
              </g>
            );
          })}

          {/* ===== CENTER CIRCLE (Results) ===== */}
          <circle cx={CX} cy={CY} r="83" fill="url(#centerGrad)"
            stroke="rgba(0,200,255,0.15)" strokeWidth="1" />

          {/* Selected CFM */}
          <text x={CX} y={CY - 45} textAnchor="middle"
            fill="#6080a0" fontSize="9" fontFamily="system-ui, sans-serif">
            CFM
          </text>
          <text x={CX} y={CY - 28} textAnchor="middle"
            fill="#00c8ff" fontSize="22" fontWeight="700"
            fontFamily="system-ui, sans-serif" filter="url(#duct-glow)">
            {selectedCFM}
          </text>

          {/* Divider */}
          <line x1={CX - 50} y1={CY - 18} x2={CX + 50} y2={CY - 18}
            stroke="rgba(255,255,255,0.1)" strokeWidth="0.5" />

          {/* Duct Diameter */}
          <text x={CX} y={CY - 4} textAnchor="middle"
            fill="#6080a0" fontSize="8" fontFamily="system-ui, sans-serif">
            ROUND DUCT
          </text>
          <text x={CX} y={CY + 16} textAnchor="middle"
            fill="#ff6b35" fontSize="28" fontWeight="800"
            fontFamily="system-ui, sans-serif">
            {results.standardD}"
          </text>
          <text x={CX} y={CY + 30} textAnchor="middle"
            fill="#506070" fontSize="8" fontFamily="system-ui, sans-serif">
            (exact: {results.exactD.toFixed(1)}")
          </text>

          {/* Divider */}
          <line x1={CX - 50} y1={CY + 38} x2={CX + 50} y2={CY + 38}
            stroke="rgba(255,255,255,0.1)" strokeWidth="0.5" />

          {/* Velocity */}
          <text x={CX} y={CY + 52} textAnchor="middle"
            fill="#6080a0" fontSize="8" fontFamily="system-ui, sans-serif">
            VELOCITY
          </text>
          <text x={CX} y={CY + 67} textAnchor="middle"
            fill={velocityColor} fontSize="16" fontWeight="700"
            fontFamily="system-ui, sans-serif">
            {results.velocity} FPM
          </text>
          {/* Noise badge */}
          <rect
            x={CX - 22} y={CY + 72}
            width="44" height="14" rx="7"
            fill={velocityColor} fillOpacity="0.15"
            stroke={velocityColor} strokeWidth="0.5" strokeOpacity="0.4"
          />
          <text x={CX} y={CY + 82} textAnchor="middle"
            fill={velocityColor} fontSize="7" fontWeight="600"
            fontFamily="system-ui, sans-serif">
            {velocityLabel}
          </text>
        </svg>

        {/* ===== Friction Rate Control ===== */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '5px',
          margin: '8px 0',
          flexWrap: 'wrap',
        }}>
          <span style={{ color: '#6080a0', fontSize: '10px', marginRight: '2px' }}>
            FRICTION:
          </span>
          {FRICTION_RATES.map((fr) => (
            <button
              key={fr}
              onClick={() => setFrictionRate(fr)}
              style={{
                padding: '4px 7px',
                background: frictionRate === fr ? 'rgba(0,200,255,0.2)' : 'rgba(255,255,255,0.05)',
                border: `1px solid ${frictionRate === fr ? 'rgba(0,200,255,0.4)' : 'rgba(255,255,255,0.1)'}`,
                borderRadius: '4px',
                color: frictionRate === fr ? '#00c8ff' : '#8090a0',
                fontSize: '10px',
                fontWeight: frictionRate === fr ? '600' : '400',
                cursor: 'pointer',
              }}
            >
              {fr}
            </button>
          ))}
          <span style={{ color: '#506070', fontSize: '8px' }}>
            in.wg/100ft
          </span>
        </div>

        {/* ===== Rectangular Equivalents ===== */}
        {results.rectEquivs.length > 0 && (
          <div style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: '6px',
            padding: '8px 10px',
          }}>
            <div style={{
              color: '#6080a0',
              fontSize: '9px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '6px',
            }}>
              Rectangular Equivalents for {results.standardD}" Round
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '4px',
            }}>
              {results.rectEquivs.slice(0, 6).map((eq, i) => (
                <div key={i} style={{
                  padding: '4px 6px',
                  background: 'rgba(255,255,255,0.05)',
                  borderRadius: '3px',
                  textAlign: 'center',
                  fontSize: '11px',
                  color: '#d0d8e0',
                  fontWeight: '500',
                }}>
                  {eq.w}" x {eq.h}"
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Hint */}
        <div style={{
          color: '#405060',
          fontSize: '9px',
          textAlign: 'center',
          marginTop: '8px',
        }}>
          Drag the outer ring to select CFM
        </div>
      </div>
    </FloatingPanel>
  );
}
